function num(v: any): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export interface IdentitasToAsesmenBbl {
  berat_lahir_gram: number | null;
  panjang_badan_cm: number | null;
  lingkar_kepala_cm: number | null;
  lingkar_dada_cm: number | null;
  jam_lahir: Date | null;
  jenis_kelahiran: string | null;
  status_bayi: string | null;
}

export function identitasToAsesmenBbl(d: any): IdentitasToAsesmenBbl {
  d = d || {};
  const berat = num(d.berat_badan);
  let jamLahir: Date | null = null;
  if (d.tanggal_lahir && d.jam_kelahiran) {
    const dt = new Date(`${String(d.tanggal_lahir).slice(0, 10)}T${d.jam_kelahiran}`);
    if (!Number.isNaN(dt.getTime())) jamLahir = dt;
  }
  return {
    berat_lahir_gram: berat === null ? null : Math.round(berat),
    panjang_badan_cm: num(d.panjang_badan),
    lingkar_kepala_cm: num(d.lingkar_kepala),
    lingkar_dada_cm: num(d.lingkar_dada),
    jam_lahir: jamLahir,
    jenis_kelahiran: d.jenis_kelahiran || null,
    status_bayi: d.status_kelahiran || null
  };
}

export function identitasToPerinatal(d: any): { bbl: number | null; panjang_badan: number | null } {
  d = d || {};
  return { bbl: num(d.berat_badan), panjang_badan: num(d.panjang_badan) };
}

function apgarJumlah(a: any): number | null {
  a = a || {};
  const j = num(a.jumlah);
  if (j !== null) return j;
  const komps = [a.frekuensi_jantung, a.usaha_bernafas, a.tonus_otot, a.iritabilitas_refleks, a.warna_kulit].map(num);
  if (komps.some(v => v === null)) return null;
  return komps.reduce((s: number, v) => s + (v as number), 0);
}

export function identitasApgarTotals(d: any): { menit_1: number | null; menit_5: number | null } {
  d = d || {};
  return { menit_1: apgarJumlah(d.apgar_1_menit), menit_5: apgarJumlah(d.apgar_5_menit) };
}

const APGAR_KOMPONEN_MAP: Record<string, string> = {
  frekuensi_jantung: "djantung",
  usaha_bernafas: "napas",
  tonus_otot: "tonus",
  iritabilitas_refleks: "reflex",
  warna_kulit: "warna"
};

function komponenToBbl(a: any): Record<string, number> | null {
  a = a || {};
  const out: Record<string, number> = {};
  let ada = false;
  Object.keys(APGAR_KOMPONEN_MAP).forEach(src => {
    const v = num(a[src]);
    if (v !== null) {
      out[APGAR_KOMPONEN_MAP[src]] = v;
      ada = true;
    }
  });
  return ada ? out : null;
}

export function identitasApgarKomponen(d: any): {
  menit_1: Record<string, number> | null;
  menit_5: Record<string, number> | null;
} {
  d = d || {};
  return { menit_1: komponenToBbl(d.apgar_1_menit), menit_5: komponenToBbl(d.apgar_5_menit) };
}

export function parseUmurTahun(usia: any): number | null {
  const n = parseInt(String(usia === null || usia === undefined ? "" : usia), 10);
  return Number.isNaN(n) || n < 0 ? null : n;
}

const PENDIDIKAN_BLANKO: Record<string, string> = {
  "tidak sekolah": "Belum/Tidak Tamat SD",
  "belum tamat sd": "Belum/Tidak Tamat SD",
  "tamat sd": "SD",
  "belum tamat smp": "SD",
  "tamat smp": "SLTP",
  "belum tamat sma": "SLTP",
  "tamat sma": "SLTA",
  "belum tamat ak/univ": "SLTA",
  "tamat ak/univ": "Perguruan Tinggi"
};

export function mapPendidikanBlanko(label: any): string {
  return PENDIDIKAN_BLANKO[String(label || "").trim().toLowerCase()] || "";
}

const PEKERJAAN_BLANKO: Record<string, string> = {
  pns: "PNS",
  abri: "TNI",
  tni: "TNI",
  swasta: "Swasta",
  dagang: "Pedagang",
  tani: "Petani",
  buruh: "Buruh"
};

export function mapPekerjaanBlanko(label: any): { pekerjaan: string; lainnya: string } {
  const raw = String(label || "").trim();
  if (!raw || raw === "-" || /tidak tahu/i.test(raw)) return { pekerjaan: "", lainnya: "" };
  const direct = PEKERJAAN_BLANKO[raw.toLowerCase()];
  if (direct) return { pekerjaan: direct, lainnya: "" };
  return { pekerjaan: "Lainnya", lainnya: raw };
}

export function mapAgamaBlanko(label: any): string {
  const raw = String(label || "").trim();
  if (!raw || raw === "-") return "";
  const opsi = ["Islam", "Protestan", "Katolik", "Hindu", "Budha"];
  const exact = opsi.find(o => o.toLowerCase() === raw.toLowerCase());
  if (exact) return exact;
  if (/katholik/i.test(raw)) return "Katolik";
  if (/kristen/i.test(raw)) return "Protestan";
  return "Lain-lain";
}

export interface IdentitasIbuPrefill {
  nama_ibu: string;
  umur_ibu: number | null;
  pekerjaan_ibu: string;
  pekerjaan_ibu_lainnya: string;
  pendidikan_ibu: string;
  agama_ibu: string;
  alamat: string;
}

export function identitasIbuFromPasien(p: any): IdentitasIbuPrefill | null {
  if (!p || !p.nama) return null;
  const nama = String(p.nama).trim();
  if (/^(bayi|by)\b/i.test(nama)) return null;
  if (p.jk && !/^p/i.test(String(p.jk))) return null;
  const umur = parseUmurTahun(p.usia);
  if (umur !== null && umur < 10) return null;
  const pkj = mapPekerjaanBlanko(p.pekerjaan);
  return {
    nama_ibu: nama,
    umur_ibu: umur,
    pekerjaan_ibu: pkj.pekerjaan,
    pekerjaan_ibu_lainnya: pkj.lainnya,
    pendidikan_ibu: mapPendidikanBlanko(p.pendidikan),
    agama_ibu: mapAgamaBlanko(p.agama),
    alamat: p.alamat ? String(p.alamat) : ""
  };
}
